import { useState } from 'react';
import { Location, useLocation, useParams } from 'react-router-dom';
import { Button, Modal, Select } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import { useGetRole, useUserCompaniesData } from '../../../../Hooks/TT_ELD/UserCompanies';
import { userCompany } from '../../../../API/TT_ELD/userCompany';
import { sendEvent } from '../../../../Utils/analytics';
import useRouteQuery from '../../../../Utils/useRouterQuery';
import { useTagQuerySync } from '../../../../Utils/useTagQuerySync';

import UserCompaniesTable from './UserCompaniesTable';
import AddMultiUserModal from './AddMultiUserModal';
import SearchOptions from '../../../../Utils/SearchOptions';
import {
  SearchForUserCompanyDR,
  SearchForUserCompanyEM,
  SearchResultForCompany,
} from '../../../../Utils/SearchResults';

const UserCompanies = ({ uid }: { uid?: string }) => {
  const { id } = useParams();
  const [skip, setSkip] = useRouteQuery('1', 'skip', String);
  const [driverId, setDriverId] = useState(uid);
  const [companyId, setCompanyId] = useState(uid ? '' : id);
  const [companyName, setCompanyName] = useRouteQuery('', 'companyName', String);
  const [role, setRole] = useRouteQuery('', 'role', String);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalBulk, setIsModalBulk] = useState(false);
  const [open, setOpen] = useState(false);
  const [getKey, setGetKey] = useState<number[] | string | number>([]);
  const [modal, contextHolder] = Modal.useModal();

  let location: Location = useLocation();

  useTagQuerySync(
    { en_US: 'User Companies', zh_CN: '' },
    'user-companies',
    { companyName, companyId, role, skip },
    { companyName: setCompanyName, companyId: setCompanyId, role: setRole, skip: setSkip },
  );

  const { data, refetch, isLoading, isFetching } = useUserCompaniesData(
    id,
    companyId,
    role,
    driverId,
    skip,
  );

  const getRole = useGetRole();
  const roleList = getRole?.data?.data
    ? Object.entries(getRole.data.data).map(([value, label]) => ({ value, label }))
    : [];

  const filteredCompanies = data?.data?.map((u: any) => ({
    value: u?.company?.id,
    label: u?.company?.name,
  }));

  const getCMPname = filteredCompanies?.[0];

  const confirmDelete = () => {
    modal.confirm({
      title: 'Delete Companies',
      icon: <ExclamationCircleOutlined />,
      okText: 'Delete',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk: async () => {
        try {
          await userCompany.deleteUserCompanyMany({ userCompanyIds: getKey });
          sendEvent('UserCompanies Bulk edit Delete', 'Delete UserCompanies', '', 1);
        } finally {
          refetch();
        }
      },
    });
  };

  const handleSearchSelect =
    (setter: (val: string | undefined) => void, nameSetter?: (val: string) => void) =>
    (val: any, { valId }: { valId: string }) => {
      setter(valId);
      nameSetter?.(val);
      if (valId) setSkip('1');
    };

  const disable = location.pathname === '/user-companies';
  let none = true;

  if (!uid && !disable && id) {
    none = false;
  }
  return (
    <div className="mainPage">
      {contextHolder}
      {isModalVisible && (
        <AddMultiUserModal
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
          userId={driverId}
          companyId={companyId}
          refetch={refetch}
          id={id}
          getCMPname={getCMPname}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {!uid && (
            <SearchOptions
              SearchResult={SearchForUserCompanyDR}
              onSelect={handleSearchSelect(setDriverId)}
              placeholder="Search by username"
            />
          )}
          {!uid && (
            <SearchOptions
              SearchResult={SearchForUserCompanyEM}
              onSelect={handleSearchSelect(setDriverId)}
              placeholder="Search by email"
            />
          )}
          {none && (
            <SearchOptions
              SearchResult={SearchResultForCompany}
              value={companyName}
              onSelect={handleSearchSelect(setCompanyId, setCompanyName)}
              placeholder="Search by Company"
            />
          )}
          <Select
            showSearch
            allowClear
            style={{ width: 200 }}
            placeholder="Select role"
            value={role || undefined}
            options={roleList}
            onChange={(val) => setRole(val ?? '')}
            optionFilterProp="children"
            filterOption={(input, option: any) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button type="primary" size="large" onClick={() => setIsModalVisible(true)}>
            Add User
          </Button>
          <Button size="large" onClick={() => refetch()} style={{ marginLeft: 15 }}>
            Refresh
          </Button>
          {isModalBulk && (
            <Button type="primary" size="large" style={{ marginLeft: 15 }} onClick={confirmDelete}>
              Delete All
            </Button>
          )}
        </div>
      </div>

      <UserCompaniesTable
        userCompanies={data?.data}
        total={data?.count}
        onChange={(q) => setSkip(q.current)}
        isLoading={isLoading}
        isFetching={isFetching}
        open={open}
        setOpen={setOpen}
        setIsModalBulk={setIsModalBulk}
        refetch={refetch}
        skip={skip}
        setGetKey={setGetKey}
      />
    </div>
  );
};

export default UserCompanies;
